import { useState, useEffect } from 'react';
import { Handshake, Search, Filter, Plus, Eye, Pencil, Trash2, Calendar, Users, Building2, Check, X, Link as LinkIcon, Copy } from 'lucide-react';
import { Dialog } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';
import { Collab, collabsRef, getDocuments, deleteDocument, updateDocument } from '../lib/firebase';
import { useTheme } from '../contexts/ThemeContext';

const COLLAB_TYPES = [
  { value: 'collaboration_entrante', label: 'Collaboration entrante' },
  { value: 'collaboration_sortante', label: 'Collaboration sortante' },
  { value: 'casting_interne', label: 'Casting interne' },
  { value: 'casting_externe', label: 'Casting externe' },
] as const;

export default function Collaborations() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [collabs, setCollabs] = useState<Collab[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedCollab, setSelectedCollab] = useState<Collab | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [copiedLink, setCopiedLink] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const sortCollabsByDate = (collabs: Collab[]) => {
    return [...collabs].sort((a, b) => {
      // Convertir les dates en timestamps pour la comparaison
      const dateA = a.createdAt ? 
        (typeof a.createdAt === 'object' && 'seconds' in a.createdAt) ? 
          a.createdAt.seconds * 1000 : 
          new Date(a.createdAt).getTime() : 
        0;
      
      const dateB = b.createdAt ? 
        (typeof b.createdAt === 'object' && 'seconds' in b.createdAt) ? 
          b.createdAt.seconds * 1000 : 
          new Date(b.createdAt).getTime() : 
        0;
      
      // Tri décroissant (plus récent en premier)
      return dateB - dateA;
    });
  };

  const loadData = async () => {
    try {
      setIsLoading(true);
      
      // Récupérer les collaborations depuis Firestore
      let collabsData = await getDocuments<Collab>(collabsRef);
      
      // Filtrer les doublons par ID
      const uniqueIds = new Set();
      collabsData = collabsData.filter(collab => {
        if (!collab.id || uniqueIds.has(collab.id)) {
          return false;
        }
        uniqueIds.add(collab.id);
        return true;
      });
      
      console.log(`Collaborations chargées: ${collabsData.length} (après déduplication)`);
      // Trier les collaborations par date (plus récentes en premier)
      setCollabs(sortCollabsByDate(collabsData));
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      if (!selectedCollab?.id) return;
      console.log(`Tentative de suppression de la collaboration: ${selectedCollab.id} (${selectedCollab.name}) - ${new Date().toISOString()}`);
      
      // Mettre à jour l'état local immédiatement pour une meilleure UX
      setCollabs(prevCollabs => {
        console.log(`Filtrage des collaborations: ${prevCollabs.length} avant suppression locale`);
        const filtered = prevCollabs.filter(collab => collab.id !== selectedCollab.id);
        console.log(`${filtered.length} collaborations après suppression locale`);
        return filtered;
      });
      
      // Fermer le modal de confirmation
      setIsDeleteModalOpen(false);
      setSelectedCollab(null);
      
      // Supprimer la collaboration de Firestore (après mise à jour de l'UI)
      try {
        const result = await deleteDocument(collabsRef, selectedCollab.id);
        console.log(`Résultat de la suppression:`, result);
        console.log(`Collaboration ${selectedCollab.id} supprimée avec succès`);
        
        // Recharger les données pour s'assurer de la synchronisation
        setTimeout(() => {
          console.log("Rechargement des données après suppression");
          loadData();
        }, 2000); // Augmenter le délai pour s'assurer que Firestore a bien traité la suppression
      } catch (deleteError) {
        console.error('Erreur lors de la suppression de la collaboration:', deleteError);
        
        // Si le document n'existe plus, on met à jour l'état local quand même
        if (deleteError instanceof Error && deleteError.message.includes('not found')) {
          console.log("Document déjà supprimé, mise à jour de l'état local");
          setCollabs(prevCollabs => prevCollabs.filter(collab => collab.id !== selectedCollab.id));
          
          // Recharger les données du serveur pour synchroniser
          setTimeout(() => {
            console.log("Rechargement des données après erreur 'not found'");
            loadData();
          }, 2000);
        }
      }
    } catch (error) {
      console.error('Erreur générale lors de la suppression:', error);
    }
  };

  const handleViewDetails = (collab: Collab) => {
    navigate(`/app/collaborations/${collab.id}`);
  };

  const handleEdit = (collab: Collab, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/app/collaborations/${collab.id}`);
  };

  const copyPublicLink = async (collab: Collab, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!collab.id || !collab.linkToken) {
      try {
        const linkToken = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
        
        // Mettre à jour la collaboration avec le token et activer le lien
        const updatedCollab = await updateDocument(collabsRef, collab.id!, {
          linkToken,
          linkActive: true,
          updatedAt: new Date()
        });
        
        // Mettre à jour l'état local
        setCollabs(prevCollabs => 
          prevCollabs.map(c => 
            c.id === collab.id 
              ? { ...c, linkToken, linkActive: true } 
              : c
          )
        );
        
        const publicUrl = `${window.location.origin}/suivi/${linkToken}`;
        await navigator.clipboard.writeText(publicUrl);
        setCopiedLink(collab.id);
        setTimeout(() => setCopiedLink(null), 3000);
        
        await loadData();
      } catch (error) {
        console.error('Error generating link:', error);
      }
      return;
    }
    
    try {
      const publicUrl = `${window.location.origin}/suivi/${collab.linkToken}`;
      await navigator.clipboard.writeText(publicUrl);
      setCopiedLink(collab.id);
      setTimeout(() => setCopiedLink(null), 3000);
    } catch (error) {
      console.error('Error copying link:', error);
    }
  };

  const formatDate = (date: Date | { seconds: number } | undefined) => {
    if (!date) return 'Non définie';
    
    try {
      if (typeof date === 'string') {
        return new Date(date).toLocaleDateString('fr-FR', {
          day: 'numeric',
          month: 'long', 
          year: 'numeric'
        });
      }
      
      if (typeof date === 'object' && date !== null && 'seconds' in date) {
        return new Date(date.seconds * 1000).toLocaleDateString('fr-FR', {
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        });
      }
      
      return new Date(date).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', date, error);
      return 'Date invalide';
    }
  };

  const getStatusBadgeClass = (collab: Collab) => {
    if (collab.invoice_sent || collab.payment_received) return 'bg-green-100 text-green-800 border-green-300';
    if (collab.quote_sent || collab.contract_signed || collab.invoice_sent) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    return 'bg-blue-100 text-blue-800 border-blue-300';
  };

  const getStatusText = (collab: Collab) => {
    if (collab.invoice_sent || collab.payment_received) return 'Terminée';
    if (collab.quote_sent || collab.contract_signed || collab.invoice_sent) {
      return 'En cours';
    }
    return 'En attente';
  };

  const filteredCollabs = collabs.filter(collab => {
    const matchesSearch = 
      collab.name.toLowerCase().includes(search.toLowerCase()) ||
      collab.brand.toLowerCase().includes(search.toLowerCase());

    const status = (collab.invoice_sent || collab.payment_received) ? 'completed' : 
                  (collab.quote_sent || collab.contract_signed || collab.invoice_sent) ? 'in_progress' : 'pending';
    const matchesStatus = statusFilter === 'all' || status === statusFilter;

    const matchesType = typeFilter === 'all' || collab.type === typeFilter;

    return matchesSearch && matchesStatus && matchesType;
  });

  // Pas besoin de trier à nouveau ici car les collaborations sont déjà triées dans loadData

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-blue"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-xl ${theme === 'dark' ? 'bg-accent-blue/20' : 'bg-accent-blue/10'} flex items-center justify-center`}>
            <Handshake className="w-6 h-6 text-accent-blue" />
          </div>
          <div>
            <h1 className={`text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              Collaborations
            </h1>
            <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
              Gérez vos collaborations avec les influenceurs
            </p>
          </div>
        </div>
        <button
          onClick={() => navigate('/app/collaborations/new')}
          className="flex items-center gap-2 px-4 py-2 bg-accent-blue text-white rounded-lg hover:bg-accent-blue/90"
        >
          <Plus className="w-5 h-5" />
          Nouvelle collaboration
        </button>
      </div>

      {/* Filters */}
      <div className={`${theme === 'dark' ? 'bg-dark-50 border-dark-200' : 'bg-white border-gray-200'} rounded-xl border p-6`}>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher par nom ou marque..."
                className="w-full pl-10"
              />
            </div>
          </div>
          <div className="flex gap-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="min-w-[150px]"
            >
              <option value="all">Tous les statuts</option>
              <option value="pending">En attente</option>
              <option value="in_progress">En cours</option>
              <option value="completed">Terminées</option>
            </select>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="min-w-[180px]"
            >
              <option value="all">Tous les types</option>
              {COLLAB_TYPES.map((type) => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Collaborations Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredCollabs.map((collab) => (
          <div
            key={collab.id}
            onClick={() => handleViewDetails(collab)}
            className={`${theme === 'dark' ? 'bg-dark-50 border-dark-200 hover:bg-dark-100' : 'bg-white border-gray-200 hover:bg-gray-50'} rounded-xl border p-6 cursor-pointer transition-colors`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-1`}>
                  {collab.name}
                </h3>
                <div className="flex items-center gap-2 mb-2">
                  <Building2 className="w-4 h-4 text-gray-400" />
                  <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                    {collab.brand}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => copyPublicLink(collab, e)}
                  className={`p-2 rounded-lg ${theme === 'dark' ? 'hover:bg-dark-200' : 'hover:bg-gray-100'} transition-colors`}
                  title="Copier le lien public"
                >
                  {copiedLink === collab.id ? (
                    <Check className="w-4 h-4 text-green-500" />
                  ) : (
                    <LinkIcon className="w-4 h-4 text-gray-400" />
                  )}
                </button>
                <button
                  onClick={(e) => handleEdit(collab, e)}
                  className={`p-2 rounded-lg ${theme === 'dark' ? 'hover:bg-dark-200' : 'hover:bg-gray-100'} transition-colors`}
                >
                  <Pencil className="w-4 h-4 text-gray-400" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedCollab(collab);
                    setIsDeleteModalOpen(true);
                  }}
                  className={`p-2 rounded-lg ${theme === 'dark' ? 'hover:bg-dark-200' : 'hover:bg-gray-100'} transition-colors`}
                >
                  <Trash2 className="w-4 h-4 text-red-400" />
                </button>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  Statut
                </span>
                <span className={`px-2 py-1 rounded-full text-xs border ${getStatusBadgeClass(collab)}`}>
                  {getStatusText(collab)}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  Type
                </span>
                <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  {COLLAB_TYPES.find(type => type.value === collab.type)?.label}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  Influenceurs
                </span>
                <div className="flex items-center gap-1">
                  <Users className="w-4 h-4 text-gray-400" />
                  <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    {collab.influencers?.length || 0}
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  Budget
                </span>
                <span className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  {collab.budget ? `${collab.budget}€` : 'Non défini'}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  Date de signature
                </span>
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    {formatDate(collab.signedDate)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredCollabs.length === 0 && (
        <div className={`text-center py-12 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
          <Handshake className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <h3 className="text-lg font-medium mb-2">Aucune collaboration trouvée</h3>
          <p className="mb-4">
            {search || statusFilter !== 'all' || typeFilter !== 'all'
              ? 'Aucune collaboration ne correspond à vos critères de recherche.'
              : 'Commencez par créer votre première collaboration.'}
          </p>
          {(!search && statusFilter === 'all' && typeFilter === 'all') && (
            <button
              onClick={() => navigate('/app/collaborations/new')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-accent-blue text-white rounded-lg hover:bg-accent-blue/90"
            >
              <Plus className="w-4 h-4" />
              Créer une collaboration
            </button>
          )}
        </div>
      )}

      {/* Delete Modal */}
      <Dialog
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        className="fixed inset-0 z-50 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
          <div className={`relative ${theme === 'dark' ? 'bg-dark-50 border-dark-200' : 'bg-white border-gray-200'} rounded-xl max-w-md w-full mx-4 p-6 border shadow-lg`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <h3 className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  Supprimer la collaboration
                </h3>
                <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  Cette action est irréversible
                </p>
              </div>
            </div>
            
            <p className={`mb-6 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              Êtes-vous sûr de vouloir supprimer la collaboration "{selectedCollab?.name}" ? 
              Toutes les données associées seront définitivement perdues.
            </p>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setIsDeleteModalOpen(false)}
                className={`px-4 py-2 ${theme === 'dark' ? 'bg-dark-200 text-gray-300 hover:bg-dark-300' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} rounded-lg transition-colors`}
              >
                Annuler
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
}